import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Box,
  Avatar,
  Typography,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Dashboard,
  ChevronLeft,
  ChevronRight,
  LightMode,
  DarkMode,
  Logout,
  MonitorHeart,
  Print,
  Webhook,
  Assessment,
  ContentCopy,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 240;
const collapsedDrawerWidth = 80;

const mainMenuItems = [
  { text: 'Label Printing', icon: <Print />, path: '/app/label-printing' },
  { text: 'Shipment Tracking', icon: <Webhook />, path: '/app/tracking-webhooks' },
  { text: 'Analytics', icon: <Dashboard />, path: '/app/analytics' },
  { text: 'Reporting', icon: <Assessment />, path: '/app/reporting' },
  { text: 'Health Monitor', icon: <MonitorHeart />, path: '/app/health' },
];

// Remove admin menu items for now
const adminMenuItems: any[] = [];

interface NavigationProps {
  onDrawerToggle: (collapsed: boolean) => void;
}

const Navigation: React.FC<NavigationProps> = ({ onDrawerToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const { themeMode, toggleTheme } = useTheme();
  const { logout, user, getAuthToken } = useAuth();
  const [copySnackbar, setCopySnackbar] = useState<{
    open: boolean;
    success: boolean;
  }>({ open: false, success: false });

  const handleToggle = () => {
    const newCollapsed = !collapsed;
    setCollapsed(newCollapsed);
    onDrawerToggle(newCollapsed);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleCopyTokenForMcp = async () => {
    const token = getAuthToken();
    if (!token) {
      setCopySnackbar({ open: true, success: false });
      return;
    }
    try {
      await navigator.clipboard.writeText(token);
      setCopySnackbar({ open: true, success: true });
    } catch {
      setCopySnackbar({ open: true, success: false });
    }
  };

  const currentWidth = collapsed ? collapsedDrawerWidth : drawerWidth;

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: currentWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: currentWidth,
          boxSizing: 'border-box',
          transition: 'width 0.2s ease-in-out',
          overflowX: 'hidden',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Logo Section */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: collapsed ? 'center' : 'space-between',
        p: collapsed ? 1 : 0,
        borderBottom: 1,
        borderColor: 'divider',
        height: 65,
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          width: '100%',
        }}>
          <Avatar
            src={collapsed ? "/logo192.png" : "/shipiumlogo.png"}
            alt="Shipium Logo"
            sx={{
              width: collapsed ? 32 : 88,
              height: collapsed ? 32 : 'auto',
              transition: 'width 0.2s ease-in-out, height 0.2s ease-in-out',
              // Apply filter to make black text white in dark mode while preserving the green logo
              filter: themeMode === 'dark' ? 'brightness(0) invert(1)' : 'none',
            }}
            variant="square"
          />
        </Box>
        <Tooltip title={collapsed ? "Expand menu" : "Collapse menu"}>
          <IconButton onClick={handleToggle} size="small">
            {collapsed ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </Tooltip>
      </Box>
      
      <List sx={{ mt: 1, flexShrink: 0 }}>
        {mainMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <Tooltip 
              title={collapsed ? item.text : ""} 
              placement="right"
              disableHoverListener={!collapsed}
            >
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => navigate(item.path)}
                sx={{
                  minHeight: 48,
                  justifyContent: collapsed ? 'center' : 'initial',
                  px: 2.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: collapsed ? 0 : 3,
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!collapsed && <ListItemText primary={item.text} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>
      
      {/* Admin Menu Items */}
      <Box sx={{ mt: 'auto', flexShrink: 0 }}>
        <List>
          {adminMenuItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <Tooltip 
                title={collapsed ? item.text : ""} 
                placement="right"
                disableHoverListener={!collapsed}
              >
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => navigate(item.path)}
                  sx={{
                    minHeight: 48,
                    justifyContent: collapsed ? 'center' : 'initial',
                    px: 2.5,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: collapsed ? 0 : 3,
                      justifyContent: 'center',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!collapsed && <ListItemText primary={item.text} />}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* User Info and Actions */}
      <Box sx={{ mt: 'auto' }}>
        {/* User Info */}
        {!collapsed && user && (
          <Box sx={{ px: 2, py: 1, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary" noWrap>
              {user.username}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {user.email}
            </Typography>
          </Box>
        )}

        <List>
          {/* Theme Toggle */}
          <ListItem disablePadding>
            <Tooltip 
              title={collapsed ? (themeMode === 'dark' ? 'Light Mode' : 'Dark Mode') : ""} 
              placement="right"
              disableHoverListener={!collapsed}
            >
              <ListItemButton
                onClick={toggleTheme}
                sx={{
                  minHeight: 48,
                  justifyContent: collapsed ? 'center' : 'initial',
                  px: 2.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: collapsed ? 0 : 3,
                    justifyContent: 'center',
                  }}
                >
                  {themeMode === 'dark' ? <LightMode /> : <DarkMode />}
                </ListItemIcon>
                {!collapsed && <ListItemText primary={themeMode === 'dark' ? 'Light Mode' : 'Dark Mode'} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>

          {/* Copy token for MCP */}
          <ListItem disablePadding>
            <Tooltip
              title={
                collapsed
                  ? 'Copy token for MCP'
                  : ''
              }
              placement="right"
              disableHoverListener={!collapsed}
            >
              <ListItemButton
                onClick={handleCopyTokenForMcp}
                sx={{
                  minHeight: 48,
                  justifyContent: collapsed ? 'center' : 'initial',
                  px: 2.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: collapsed ? 0 : 3,
                    justifyContent: 'center',
                  }}
                >
                  <ContentCopy />
                </ListItemIcon>
                {!collapsed && (
                  <ListItemText primary="Copy token for MCP" />
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>

          {/* Logout */}
          <ListItem disablePadding>
            <Tooltip 
              title={collapsed ? "Logout" : ""} 
              placement="right"
              disableHoverListener={!collapsed}
            >
              <ListItemButton
                onClick={handleLogout}
                sx={{
                  minHeight: 48,
                  justifyContent: collapsed ? 'center' : 'initial',
                  px: 2.5,
                  color: 'error.main',
                  '&:hover': {
                    backgroundColor: 'error.light',
                    color: 'error.contrastText',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: collapsed ? 0 : 3,
                    justifyContent: 'center',
                    color: 'inherit',
                  }}
                >
                  <Logout />
                </ListItemIcon>
                {!collapsed && <ListItemText primary="Logout" />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        </List>
      </Box>

      <Snackbar
        open={copySnackbar.open}
        autoHideDuration={3000}
        onClose={() => setCopySnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setCopySnackbar((s) => ({ ...s, open: false }))}
          severity={copySnackbar.success ? 'success' : 'warning'}
        >
          {copySnackbar.success
            ? 'Token copied to clipboard. Use as MIDDLEWARE_AUTH_TOKEN in MCP config.'
            : 'No token available.'}
        </Alert>
      </Snackbar>
    </Drawer>
  );
};

export default Navigation;
